/**
 * ONE authored format per attribute, applied in EVERY context (05 §5, gap G3).
 *
 * This module exists because the two surfaces that render a value — the list
 * card and the detail page — were two hand-written files, and Items is where
 * they visibly disagreed: `Items.js:560` printed
 * `{price_gp.toLocaleString()} gp` and `{weight} lbs`, while `ItemDetail.js:161`
 * printed a bare `{price_gp} gp` and a bare `{weight}`. So a 2,500 gp item read
 * `2,500 gp` on the card it was clicked from and `2500 gp` on the page it
 * arrived at, and its weight lost its unit on the way.
 *
 * G3's decision is that per-context display metadata to serve an inconsistency
 * is model complexity with negative value, and that the RICHER of the two
 * renderings wins. So the format is a fact about the attribute (`format`,
 * `unit` — v2.6), it is read here, and both callers call this. Making the two
 * agree is not a policy the renderer applies; it is the only thing it can do.
 */

/**
 * `formatCR`'s fraction set, already shipped for monster CRs and named here so
 * `format: "fraction"` has a renderer when Monsters converts (R11). Kept beside
 * `locale` rather than in a second module, because "how a number is spelled" is
 * one question.
 */
const FRACTIONS = [
  [0.5, '1/2'], [1 / 3, '1/3'], [0.25, '1/4'], [1 / 6, '1/6'], [0.125, '1/8'], [0.1, '1/10'],
];
const EPSILON = 0.001;

function formatNumber(attr, value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (attr.format === 'fraction') {
    const hit = FRACTIONS.find(([v]) => Math.abs(n - v) < EPSILON);
    if (hit) return hit[1];
  }
  if (attr.format === 'locale') return n.toLocaleString();
  // A modifier shows its sign (3.5 monster saves, Base Attack, Grapple): `+6`, `-1`, `+0`.
  // Only the spelling changes; sorting and filtering read the stored number.
  if (attr.format === 'signed') return n >= 0 ? `+${n}` : String(n);
  return String(n);
}

/**
 * A scalar attribute's display text — the prefix, the number format, the unit.
 *
 * The unit is appended with a single space and the prefix is butted straight up
 * against the number, and both only apply to a NUMBER, which is what the
 * validator enforces on the way in: `3` becomes `3 lbs`, `8` becomes `d8`, and a
 * text attribute cannot acquire either by accident.
 *
 * `prefix` arrived with Classes (v2.7). `Hit Die: d8` and
 * `Base Attack Bonus: +5` are the same claim `unit` makes, on the other side of
 * the number — a fact about how the attribute is spelled, true in the detail
 * row, the list card and the filter dropdown alike.
 */
/**
 * A price under 1 gp in the coin the d20 books print it in. The stored number
 * stays in gp (it is the register's base unit, and sorting, filtering and every
 * sum read it); only the spelling changes. Pathfinder Ale is stored `0.04` and
 * the book prices it `4 cp`; a `0.5` trail ration is `5 sp`. Whole silver reads
 * in sp, anything else in cp, the way the equipment tables write it.
 *
 * At 1 gp and above the gp decimal stands (`1.5 gp`): the equipment tables
 * never print a mixed `1 gp 5 sp`, and no authored price in either built-in
 * system has a fraction above 1 gp. A value finer than a copper is not a coin
 * price, so it keeps its gp decimal too. Returns null when the rule does not
 * apply.
 */
export function subGoldCoins(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n >= 1) return null;
  const cp = Math.round(n * 100);
  if (Math.abs(cp - n * 100) > EPSILON) return null;
  return cp % 10 === 0 ? `${cp / 10} sp` : `${cp} cp`;
}

/**
 * A bare ISO date in a text value as a reader writes it: rulebooks store
 * `published: "2003-07-01"`. The corpus records month-precision dates on the
 * 1st, so a 1st reads as the month ("July 2003") rather than claiming a day;
 * any other day is printed ("November 7, 2006"). UTC, so no time zone moves it
 * a day. Returns null for anything that is not exactly YYYY-MM-DD.
 */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];
export function readableDate(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!m) return null;
  const [, y, mo, d] = m.map(Number);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return d === 1 ? `${MONTHS[mo - 1]} ${y}` : `${MONTHS[mo - 1]} ${d}, ${y}`;
}

/**
 * A coded text value's display word (`render_config.value_labels`): 3.5 items
 * store `type: "MUN"` and `slot: "held"`, which the Shop and Treasure
 * generators and the magic-arms composer read, so the stored code stays and the
 * reader sees the book's word ("Mundane", "Held"). Only the spelling changes;
 * filtering, URLs and every consumer of the stored value read the code.
 * Returns null when the attribute labels no such value.
 */
export function valueLabel(attr, value) {
  const labels = attr && attr.renderConfig && attr.renderConfig.value_labels;
  if (!labels || typeof value !== 'string') return null;
  return Object.prototype.hasOwnProperty.call(labels, value) ? labels[value] : null;
}

export default function formatScalar(attr, value) {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const labelled = valueLabel(attr, value);
  if (labelled !== null) return labelled;
  const numeric = attr && attr.datatype === 'number';
  if (numeric && attr.unit === 'gp' && !attr.prefix) {
    const coins = subGoldCoins(value);
    if (coins) return coins;
  }
  const text = numeric ? formatNumber(attr, value) : (readableDate(value) || String(value));
  const prefixed = numeric && attr.prefix ? `${attr.prefix}${text}` : text;
  return attr && attr.unit ? `${prefixed} ${attr.unit}` : prefixed;
}
